/**
 * The seed corpus, validated at load (TASKS T37).
 *
 * Importing this module runs the shape check over every item and **throws** on any problem. That is
 * deliberate: a corpus that is half-valid is worse than one that fails to load, because the missing
 * half looks exactly like content that was never written. `scripts/check-content.ts` runs the same
 * validation at build time so the failure lands in CI rather than in front of the learner.
 *
 * Nothing here reads a clock or a store. `toItemRecord` mints the runtime record at intake, when
 * the caller supplies `now` from the injected clock.
 */

import casaRaw from './casa.json';
import tiempoRaw from './tiempo.json';
import sonidoRaw from './sonido.json';
import nucleoRaw from './nucleo.json';
import patternsRaw from './patterns.json';
import rulesRaw from './rules.json';
import verdictsRaw from './verdicts.json';
import {
  hazardItems,
  isQuarantined,
  itemsFromPatterns,
  itemsFromRules,
  loadSeedItems,
  type ConversionPattern,
  type ConversionRule,
  type PatternFile,
  type RuleFile,
  type SeedItem,
  type VerdictFile,
} from './schema';

/**
 * Tier 1: the sound inventory (T62).
 *
 * Speech-only, and therefore invisible on a device with no microphone — `playableModes` returns an
 * empty list for these items, which removes them from intake, and `playableQueue` drops them from
 * the due queue. Nothing else has to know tier 1 exists (DoD-4).
 */
export const SONIDO: readonly SeedItem[] = loadSeedItems(sonidoRaw as unknown[], 'sonido.json');

/** Tier 2 and 3: the ~300 frequency items. */
export const NUCLEO: readonly SeedItem[] = loadSeedItems(nucleoRaw as unknown[], 'nucleo.json');

/** Tier 5: household and infant-care phrases. */
export const CASA: readonly SeedItem[] = loadSeedItems(casaRaw as unknown[], 'casa.json');

/**
 * Tier 6: the tense ladder — preterite, then imperfect, then subjunctive.
 *
 * Every item here carries a `derivation`, and the schema requires it at this tier. The preterite
 * and the subjunctive are large regular systems, and a learner either derives them or memorises a
 * hundred forms one at a time; a tense item with no derivation would be the second thing wearing
 * the clothes of the first.
 */
export const TIEMPO: readonly SeedItem[] = loadSeedItems(tiempoRaw as unknown[], 'tiempo.json');

export const RULE_FILE = rulesRaw as unknown as RuleFile;
export const PATTERN_FILE = patternsRaw as unknown as PatternFile;

export const RULES: readonly ConversionRule[] = RULE_FILE.rules;
export const PATTERNS: readonly ConversionPattern[] = PATTERN_FILE.patterns;

/**
 * Tier 4: everything the conversion engine contributes — the worked examples, the constructive
 * patterns, and the **hazards**, which are items in their own right (T52).
 *
 * Hazards last, and after the examples in id order, because intake offers by tier then id: a rule's
 * worked examples are introduced before the words that rule does not reach.
 */
export const CONVERSION: readonly SeedItem[] = loadSeedItems(
  [...itemsFromRules(RULE_FILE), ...itemsFromPatterns(PATTERN_FILE), ...hazardItems(RULE_FILE)],
  'rules.json + patterns.json',
);

/** Layer-1 verdicts, written by `scripts/vet-content.ts`. Empty until that has been run. */
export const VERDICTS = verdictsRaw as unknown as VerdictFile;

/**
 * Every seed item, in one list, with its verdict attached.
 *
 * Validated once more as a whole, because duplicate ids across two files are invisible to a
 * per-file check and the store is keyed by id.
 *
 * `vetted` stays `false` here no matter what the verdict says — the Diana gate is a human action
 * and this is a filter (BRIEF <spanish_correctness>).
 */
export const CORPUS: readonly SeedItem[] = loadSeedItems(
  [...SONIDO, ...NUCLEO, ...CONVERSION, ...CASA, ...TIEMPO],
  'corpus',
).map((item) => ({ ...item, verdict: VERDICTS.verdicts[item.id] ?? null }));

/**
 * The items the game may actually draw.
 *
 * Anything the vetting pass failed is excluded here rather than filtered at draw time, so there is
 * exactly one place a quarantined item can leak from and it is this line. Nothing downstream —
 * queue, round builder, intake — needs to know quarantine exists.
 */
export const SERVED: readonly SeedItem[] = CORPUS.filter((item) => !isQuarantined(item.verdict));

/** Items held back by layer 1, for the report the vetting script and the review screen show. */
export const QUARANTINED: readonly SeedItem[] = CORPUS.filter((item) => isQuarantined(item.verdict));

const BY_ID = new Map(CORPUS.map((item) => [item.id, item]));

export function seedItem(id: string): SeedItem | undefined {
  return BY_ID.get(id);
}

/** Only ending rules generate words for free; a false-friend set generates nothing (D-075). */
export function generativeRules(): readonly ConversionRule[] {
  return RULES.filter((rule) => rule.kind === 'ending');
}

const RULE_BY_ID = new Map(RULES.map((rule) => [rule.id, rule]));

/** The rule an item exercises, for the card that presents it (T52). */
export function conversionRule(ruleId: string): ConversionRule | undefined {
  return RULE_BY_ID.get(ruleId);
}

const PATTERN_BY_ID = new Map(PATTERNS.map((pattern) => [pattern.id, pattern]));

/** The constructive pattern an item exercises. A tier-4 item names one or the other, never both. */
export function conversionPattern(patternId: string): ConversionPattern | undefined {
  return PATTERN_BY_ID.get(patternId);
}

// Both of these draw from SERVED, never CORPUS: a quarantined item must not reach a round through
// a convenience accessor.

export function itemsForRule(ruleId: string): readonly SeedItem[] {
  return SERVED.filter((item) => item.rule === ruleId);
}

export function itemsForTier(tier: SeedItem['tier']): readonly SeedItem[] {
  return SERVED.filter((item) => item.tier === tier);
}
